import { useCallback, useEffect, useState } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Label } from '../ui/label'
import { Select } from '../ui/Select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { createStoreWithPlan } from '../../services/storeService'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'

function slugifyStoreName(name: string) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
}

type AddStoreDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddStoreDialog({ open, onOpenChange }: AddStoreDialogProps) {
  const { refreshStores, setCurrentStoreId } = useCurrentStore()
  const [name, setName] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [subdomainTouched, setSubdomainTouched] = useState(false)
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [sellingSummary, setSellingSummary] = useState('')
  const [planCode, setPlanCode] = useState<'standard' | 'premium' | 'plus'>('standard')
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName('')
    setSubdomain('')
    setSubdomainTouched(false)
    setContactEmail('')
    setContactPhone('')
    setSellingSummary('')
    setPlanCode('standard')
    setBillingInterval('monthly')
    setError(null)
  }, [open])

  useEffect(() => {
    if (subdomainTouched) return
    setSubdomain(slugifyStoreName(name))
  }, [name, subdomainTouched])

  const onSubmit = useCallback(async () => {
    const trimmedName = name.trim()
    const trimmedSubdomain = subdomain.trim().toLowerCase()
    if (!trimmedName || !trimmedSubdomain) {
      setError('Store name and subdomain are required.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const created = await createStoreWithPlan({
        planCode,
        billingInterval,
        store: {
          name: trimmedName,
          subdomain: trimmedSubdomain,
          contactEmail: contactEmail.trim() || undefined,
          contactPhone: contactPhone.trim() || undefined,
          sellingSummary: sellingSummary.trim() || undefined,
        },
      })
      await refreshStores()
      setCurrentStoreId(created.id)
      onOpenChange(false)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not create store'))
    } finally {
      setSubmitting(false)
    }
  }, [
    name,
    subdomain,
    contactEmail,
    contactPhone,
    sellingSummary,
    planCode,
    billingInterval,
    refreshStores,
    setCurrentStoreId,
    onOpenChange,
  ])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" showCloseButton>
        <DialogHeader>
          <DialogTitle>Add store</DialogTitle>
          <DialogDescription>
            Create another storefront under your organisation. You will complete billing setup for the new store
            separately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="add-store-name">Store name</Label>
            <Input
              id="add-store-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My second shop"
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-store-subdomain">Subdomain</Label>
            <Input
              id="add-store-subdomain"
              value={subdomain}
              onChange={(e) => {
                setSubdomainTouched(true)
                setSubdomain(e.target.value)
              }}
              placeholder="my-second-shop"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">Used in your store URL (e.g. my-second-shop.yourdomain.com).</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-store-email">Storefront contact email</Label>
            <Input
              id="add-store-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="hello@yourstore.com"
              autoComplete="email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-store-phone">Storefront contact phone</Label>
            <Input
              id="add-store-phone"
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+1 555 123 4567"
              autoComplete="tel"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-store-selling">What this shop sells</Label>
            <textarea
              id="add-store-selling"
              value={sellingSummary}
              onChange={(e) => setSellingSummary(e.target.value)}
              rows={3}
              maxLength={5000}
              placeholder="Products, niche, who you sell to…"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="add-store-plan">Plan</Label>
              <Select
                id="add-store-plan"
                value={planCode}
                onChange={(e) => setPlanCode(e.target.value as 'standard' | 'premium' | 'plus')}
              >
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
                <option value="plus">Plus</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-store-interval">Billing</Label>
              <Select
                id="add-store-interval"
                value={billingInterval}
                onChange={(e) => setBillingInterval(e.target.value as 'monthly' | 'yearly')}
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </Select>
            </div>
          </div>
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void onSubmit()} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create store'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
